import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Attendance from './pages/Attendance';
import QrKiosk from './pages/QrKiosk';
import StudentCardPrint from './pages/StudentCardPrint';
import ClassCardsPrint from './pages/ClassCardsPrint';
import Teachers from './pages/Teachers';
import TeachersCardsPrint from './pages/TeachersCardsPrint';
import MonthlyRecap from './pages/MonthlyRecap';
import ClassData from './pages/ClassData';
import Settings from './pages/Settings';
import Profile from './pages/Profile';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = ['admin'] }) => {
  const { isAuthenticated, loading, userRole } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return userRole === 'guru' ? <Navigate to="/kiosk-qr" /> : <Navigate to="/login" />;
  }
  
  return children;
};

// Main App Component
const AppContent = () => {
  const { isAuthenticated, userRole } = useAuth();

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={isAuthenticated ? (userRole === 'guru' ? <Navigate to="/kiosk-qr" /> : <Navigate to="/dashboard" />) : <Login />} 
        />
        <Route path="/kiosk-qr" element={<QrKiosk />} />
        <Route path="/auto-attendance" element={<Navigate to="/kiosk-qr" replace />} />
        <Route path="/kiosk-attendance" element={<Navigate to="/kiosk-qr" replace />} />
        <Route path="/face-registration" element={<Navigate to="/students" replace />} />
        <Route
          path="/students/:id/card"
          element={
            <ProtectedRoute>
              <StudentCardPrint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/students/print-class"
          element={
            <ProtectedRoute>
              <ClassCardsPrint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teachers/:id/card"
          element={
            <ProtectedRoute>
              <StudentCardPrint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teachers/print-all"
          element={
            <ProtectedRoute>
              <TeachersCardsPrint />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/students"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <Students />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teachers"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <Teachers />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <Attendance />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/monthly-recap"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <MonthlyRecap />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/classes"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <ClassData />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Router>
  );
};

// Root App Component
const App = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
